/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';

import ReactPaginate from 'react-paginate';

import { isMobile } from './../../components/breakpoints';
import Popup from './../../components/popup';

import Carousel from 'react-multi-carousel';

import { IProgrammeCard, ProgrammeCard } from './components/ecomm-programme-card';
import { IEcommUpcomingProgrammesViewProps } from './ecomm-upcoming-programmes';

/**
 *
 * EcommUpcomingProgrammes component
 * @extends {React.PureComponent<IEcommUpcomingProgrammesProps<IEcommUpcomingProgrammesData>>}
 */
 class EcommUpcomingProgrammesView extends React.PureComponent<IEcommUpcomingProgrammesViewProps> {
  public state: any = { width: 0, hasMounted: false, showPopup: false, selectedMonth: '', activePage: 0, pageSize: 3 };

  public componentDidMount(): void {
    if (!this.state.hasMounted) {
      window.addEventListener('resize', this._updateDimensions);

      this.setState({ width: window.innerWidth, hasMounted: true });
    }
  }

  public render(): JSX.Element | null {
    const { config, mobileEvents, upcomingEvents } = this.props;
    
    if (!this.state.hasMounted) {
      return null;
    }

    const mobileMode = isMobile(this.state.width);
    const language = this.props.context.request.locale.split('-')[0];

    // Dropdown version on mobile for events carousel.
    if (mobileMode && config.displayMobileDropdown) {
      const months: string[] = [];
      months.push(language === 'en' ? 'All' : '全部');

      const displayMonth = this.state.selectedMonth === '' ? months[0] : this.state.selectedMonth;

      this.props.existingMonths.forEach(m => {
        if (m !== displayMonth) {
          months.push(m);
        }
      });

      const offset = this.state.activePage * Number(config.mobileDropdownPageSize);
      const showAll = displayMonth === 'All' || displayMonth === '全部' ? true : false;

      let displayEvents = [];

      if (showAll) {
        displayEvents = upcomingEvents;
      } else {
        displayEvents = mobileEvents.find(item => item.month === displayMonth)?.events!;
      }

      let pageCount = 0;
      let currentPageEvents: JSX.Element[] = [];

      if (displayEvents) {
        pageCount = Math.ceil(displayEvents.length / config.mobileDropdownPageSize);

        currentPageEvents = displayEvents.slice(offset, offset + Number(config.mobileDropdownPageSize)).map((item: IProgrammeCard) =>
          <ProgrammeCard
            id={item.id}
            type={item.type}
            name={item.name}
            startDateTime={item.startDateTime}
            endDateTime={item.endDateTime}
            description={item.description}
            img={item.img} 
            redirect={item.redirect}
            portalRedirect={config.redirectPortal}
            callback={this._handleCallbackPopup}
            buttonText={config.cardButtonText}
            isDropdownVersion={config.displayMobileDropdown}
          />
        );
      }

      return (
        <div className='ecomm-upcoming-programmes mobile'>
          <DropdownButton id='dropdown-basic-button' title={displayMonth} onSelect={this._onDropdownSelect}>
            {months.map((month: string, idx: number) =>
              <Dropdown.Item key={idx} eventKey={month}>{month}</Dropdown.Item>
            )}
          </DropdownButton>
          <div>{currentPageEvents}</div>
          <ReactPaginate
            previousLabel={'<'}
            nextLabel={'>'}
            breakLabel={'...'}
            breakClassName={'break-me'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={this._handlePageClick}
            containerClassName={'ecomm-pagination'}
            activeClassName={'active'}
          />
        </div>
      );
    }

    const responsive = {
      superLargeDesktop: {
        breakpoint: { max: 4000, min: 992 },
        items: 2,
        slidesToSlide: 2
      },
      desktop: {
        breakpoint: { max: 992, min: 768 },
        items: 2,
        slidesToSlide: 2
      },
      tablet: {
        breakpoint: { max: 768, min: 540 },
        items: 2,
        slidesToSlide: 2
      },
      mobile: {
        breakpoint: { max: 540, min: 0 },
        items: 1,
        slidesToSlide: 1
      }
    };

    const mockData = this.props.upcomingEvents;
    const bg = config.bgImage?.src ? { backgroundImage: `url(${config.bgImage.src})`} : { backgroundImage: '' };
    const spd = config.sliderSpeed ? config.sliderSpeed * 1000 : 5000; // Convert to milliseconds.
    const showArrows = mobileMode ? false : true;
    const localeCode = this.props.context.request.locale.split('-')[0];
    const calendarLink = config.calendarLink ? config.calendarLink.linkUrl.destinationUrl : '';

    return (
      <div className='ecomm-upcoming-programmes' style={bg}>
        <div className='top row'>
          <div className='title col-sm-8'><p>{config.title}</p></div>
          {!mobileMode && config.calendarLink?.linkText !== '' && <div className='nav col-sm-4'><a href={calendarLink}>{config.calendarLink?.linkText}</a></div>}
          {config.redirectPortal && <Popup showPopup={this.state.showPopup} name={config.title} url={config.portalUrl.destinationUrl} isMobile={mobileMode ? true : false} isPortalPopup={true} locale={localeCode} callback={this._handleCallback}/>}
        </div>
        <Carousel responsive={responsive} infinite={true} arrows={showArrows} autoPlay={true} autoPlaySpeed={spd}>
          {mockData.map((item: IProgrammeCard) => {
            return <ProgrammeCard
              id={item.id}
              type={item.type}
              name={item.name}
              startDateTime={item.startDateTime}
              endDateTime={item.endDateTime}
              description={item.description}
              img={item.img} 
              redirect={item.redirect}
              portalRedirect={config.redirectPortal}
              callback={this._handleCallbackPopup}
              buttonText={config.cardButtonText}
              isDropdownVersion={false}
            />;
          })}
        </Carousel>
      </div>
    );
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this._updateDimensions);
  }

  private _handleCallback = () => {
    this.setState({ showPopup: false});
  }
  
  private _handleCallbackPopup = () => {
    this.setState({ showPopup: true});
  }

  private _updateDimensions = () => {
    this.setState({ width: window.innerWidth });
  };

  private _onDropdownSelect = (event: any) => {
    this.setState({ selectedMonth: event, activePage: 0 });
  }

  private _handlePageClick = (data: any) => {
    this.setState({ activePage: data.selected });
  }
}

export default EcommUpcomingProgrammesView;